
import React, { useEffect, useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import PrivateRoute from "./auth/PrivateRoute";
import AppLoader from "./components/AppLoader";
import NotificationCenter from "./components/NotificationCenter";
import { BrandingProvider, resolveBrandingThemeTokens, useBranding } from "./contexts/BrandingContext";
import { AuthProvider } from "./contexts/AuthContext";
import { UIProvider, useUI } from "./contexts/UIContext";
import MainLayout from "./layout/MainLayout";
import Admin from "./pages/Admin";
import Dashboard from "./pages/Dashboard";
import Dipendenti from "./pages/Dipendenti";
import Dispositivi from "./pages/Dispositivi";
import Disponibilita from "./pages/Disponibilita";
import Ferie from "./pages/Ferie";
import Login from "./pages/Login";
import Locali from "./pages/Locali";
import Pagamenti from "./pages/Pagamenti";
import Turni from "./pages/Turni";
import { schedulePalette } from "./components/PageLayout";

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />

            <Route
                path="/"
                element={
                    <PrivateRoute>
                        <MainLayout />
                    </PrivateRoute>
                }
            >
                <Route index element={<Dashboard />} />
                <Route path="turni" element={<Turni />} />
                <Route
                    path="locali"
                    element={
                        <PrivateRoute allowedRoles={["SUPERADMIN","ADMIN", "MANAGER"]}>
                            <Locali />
                        </PrivateRoute>
                    }
                />
                <Route
                    path="dipendenti"
                    element={
                        <PrivateRoute allowedRoles={["SUPERADMIN","ADMIN", "MANAGER"]}>
                            <Dipendenti />
                        </PrivateRoute>
                    }
                />
                <Route
                    path="disponibilita"
                    element={
                        <PrivateRoute allowedRoles={["SUPERADMIN","ADMIN", "MANAGER"]}>
                            <Disponibilita />
                        </PrivateRoute>
                    }
                />
                <Route path="ferie" element={<Ferie />} />
                <Route
                    path="dispositivi"
                    element={
                        <PrivateRoute allowedRoles={["SUPERADMIN","ADMIN", "MANAGER", "DIPENDENTE"]}>
                            <Dispositivi />
                        </PrivateRoute>
                    }
                />
                <Route
                    path="admin"
                    element={
                        <PrivateRoute allowedRoles={["SUPERADMIN","ADMIN"]}>
                            <Admin />
                        </PrivateRoute>
                    }
                />
                <Route
                    path="pagamenti"
                    element={
                        <PrivateRoute allowedRoles={["SUPERADMIN","ADMIN"]}>
                            <Pagamenti />
                        </PrivateRoute>
                    }
                />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function ThemedApp() {
    const { themeMode } = useUI();
    const { branding } = useBranding();

    const mergedMode = branding.theme === "DARK" || (branding.theme === "CUSTOM" && themeMode === "dark") ? "dark" : themeMode;
    const {
        textColor: modeFontColor,
        foregroundColor: modeForegroundColor,
        fieldColor: modeFieldColor,
        tableCellTextColor: modeTableCellTextColor,
    } =
        resolveBrandingThemeTokens(branding, mergedMode);

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: mergedMode,
                    primary: { main: branding.colorPrimary || schedulePalette.ocean },
                    secondary: { main: branding.colorSecondary || schedulePalette.warm },
                    text: {
                        primary: modeFontColor,
                        secondary: mergedMode === "dark" ? "#94a3b8" : "#5f6f81",
                    },
                    background:
                        { default: modeForegroundColor, paper: modeForegroundColor },
                },
                shape: {
                    borderRadius: 14,
                },
                typography: {
                    fontFamily: branding.fontFamily || "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
                    fontSize: parseFloat(String(branding.appFontSize || "16px")) || 16,
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            ":root": {
                                "--app-font-family": branding.fontFamily || "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
                                "--app-font-size": branding.appFontSize || "16px",
                                "--app-table-font-family": branding.tableFontFamily || branding.fontFamily || "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
                                "--app-table-font-size": branding.tableFontSize || "0.875rem",
                                "--app-table-header-font-size": branding.tableHeaderFontSize || "0.82rem",
                                "--app-table-cell-text-color": modeTableCellTextColor || "#3498db",
                                "--app-disponibilita-text-color": modeTableCellTextColor || "#3498db",
                                "--app-reperibilita-text-color": branding.colorAccent || "#e67e22",
                                "--app-content-max-width": branding.contentMaxWidth || "100%",
                                "--app-content-padding-x": branding.contentPaddingX || "24px",
                                "--app-content-padding-y": branding.contentPaddingY || "24px",
                                "--app-page-surface-radius": branding.pageSurfaceRadius || "14px",
                                "--app-page-shell-gradient": branding.pageShellGradient || "linear-gradient(180deg, rgba(214, 234, 248, 0.55) 0%, rgba(241, 245, 249, 0.96) 26%, rgba(255, 255, 255, 1) 100%)",
                                ...(branding.customCss ? {} : {}),
                            },
                            body: {
                                fontFamily: branding.fontFamily || "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
                                fontSize: branding.appFontSize || "16px",
                                color: modeFontColor,
                                backgroundColor: modeForegroundColor,
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: "none",
                                backgroundColor: modeForegroundColor,
                                color: modeFontColor,
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                                textTransform: "none",
                                fontWeight: 700,
                                fontFamily: branding.fontFamily || "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                backgroundColor: modeFieldColor,
                                borderRadius: 12,
                            },
                        },
                    },
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                borderRadius: 24,
                                border: `1px solid ${schedulePalette.border}`,
                                boxShadow: "0 18px 40px rgba(44, 62, 80, 0.10)",
                            },
                        },
                    },
                },
            }),
        [branding, mergedMode, modeFieldColor, modeFontColor, modeForegroundColor, modeTableCellTextColor, themeMode]
    );

    useEffect(() => {
        const existingStyle = document.getElementById("branding-custom-css");
        if (existingStyle) {
            existingStyle.remove();
        }

        if (!branding.customCss) {
            return;
        }

        const styleElement = document.createElement("style");
        styleElement.id = "branding-custom-css";
        styleElement.textContent = branding.customCss;
        document.head.appendChild(styleElement);

        return () => {
            styleElement.remove();
        };
    }, [branding.customCss]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
            <NotificationCenter />
            <AppLoader />
        </ThemeProvider>
    );
}

export default function App() {
    return (
        <UIProvider>
            <AuthProvider>
                <BrandingProvider>
                    <ThemedApp />
                </BrandingProvider>
            </AuthProvider>
        </UIProvider>
    );
}
